/**
 * Centralised logging for the web app.
 *
 * Every module should prefer `createLogger('scope')` over direct `console.*`
 * calls. This gives us:
 *   - Consistent `[scope]` prefixes (grep-friendly)
 *   - A single seam to plug Sentry / PostHog into later via `installSink`
 *   - Global `window.error` + `unhandledrejection` capture for anything that
 *     escapes React boundaries
 *
 * Usage:
 *   import { createLogger } from '@/lib/logger'
 *   const log = createLogger('crm-sync')
 *   log.info('starting sync', { count })
 *   log.error('sync failed', err)
 */

import { env } from './env'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  level: LogLevel
  scope: string
  args: unknown[]
  /** First Error instance found in args, if any. */
  error?: Error
  timestamp: number
}

export type LogSink = (entry: LogEntry) => void

const sinks: LogSink[] = []

/**
 * Register a sink that receives every log entry. Returns an unsubscribe fn.
 * Wire Sentry / PostHog here once you're ready for production.
 */
export function installSink(sink: LogSink): () => void {
  sinks.push(sink)
  return () => {
    const idx = sinks.indexOf(sink)
    if (idx >= 0) sinks.splice(idx, 1)
  }
}

function emit(level: LogLevel, scope: string, args: unknown[]): void {
  const error = args.find((a): a is Error => a instanceof Error)
  const prefix = `[${scope}]`

  // Console mirroring: always show warn/error so prod bug reports still contain
  // useful stack traces. debug/info are dev-only — keeps the production console
  // clean for users running with DevTools open and avoids leaking app internals.
  const shouldMirror = env.isDev || level === 'warn' || level === 'error'
  if (shouldMirror) {
    const fn =
      level === 'error' ? console.error :
      level === 'warn' ? console.warn :
      level === 'debug' ? console.debug :
      console.log
    fn(prefix, ...args)
  }

  if (sinks.length === 0) return
  const entry: LogEntry = { level, scope, args, error, timestamp: Date.now() }
  for (const sink of sinks) {
    try {
      sink(entry)
    } catch (sinkErr) {
      // Never let a broken sink kill the caller. Report via console only.
      console.error('[logger] sink threw', sinkErr)
    }
  }
}

export interface Logger {
  debug(...args: unknown[]): void
  info(...args: unknown[]): void
  warn(...args: unknown[]): void
  error(...args: unknown[]): void
  /** Create a namespaced sub-logger, e.g. log.child('upsert') → `[crm-sync:upsert]` */
  child(subscope: string): Logger
}

export function createLogger(scope: string): Logger {
  return {
    debug: (...args) => emit('debug', scope, args),
    info: (...args) => emit('info', scope, args),
    warn: (...args) => emit('warn', scope, args),
    error: (...args) => emit('error', scope, args),
    child: (subscope) => createLogger(`${scope}:${subscope}`),
  }
}

/** Root logger for one-off use. Prefer `createLogger('yourScope')` per module. */
export const log = createLogger('fwdslash')

let globalHandlersInstalled = false

/**
 * Install `window.onerror` + `unhandledrejection` handlers so crashes outside
 * React (async callbacks, event handlers, promise rejections) still reach the
 * logger. Call once from main.tsx.
 */
export function installGlobalHandlers(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') return
  globalHandlersInstalled = true

  window.addEventListener('error', (event) => {
    // `event.error` can be null for cross-origin script errors; fall back to message.
    emit('error', 'window.error', [event.message, event.error ?? event])
  })

  window.addEventListener('unhandledrejection', (event) => {
    emit('error', 'unhandledrejection', [event.reason])
  })
}
